<template>
  <div class="ai-chat">
    <div class="scroll" ref="scrollContainer" @scroll="handleScroll">
      <HistoryConversation v-if="isAuthenticated" />
      <ChatHeader />
      <QuestionSuggestions
        :suggestions="suggestions"
        @select="onSuggestionSelect"
      />
      <ChatBody :messages="messages" />
      <!-- 相关问题组件 -->
      <RelatedQuestions
        v-if="relatedQuestions && relatedQuestions.length > 0"
        :questions="relatedQuestions"
        @select="onRelatedQuestionSelect"
      />
    </div>
    <!-- 滚动到底部按钮 -->
    <ScrollToBottomButton v-if="showScrollToBottom" @scroll="scrollToBottom" />
    <VoiceInput
      @send="handleSendMessage"
      @record-complete="handleRecordComplete"
      :voice-enabled="true"
    />
    <!-- Toast 提示组件 -->
    <Toast ref="toast" />
    
    <!-- 调试：播放最后一次录音的 MP3 -->
    <button
      v-if="lastMp3Blob && showDebugPlayBtn"
      class="debug-play-btn"
      @click="playLastMp3"
      title="播放最后一次录音转换的 MP3"
    >
      🎵 播放MP3
    </button>
  </div>
</template>

<script>
// 组件
import ChatHeader from "@/components/ChatHeader/index.vue";
import ChatBody from "@/components/ChatBody/index.vue";
import QuestionSuggestions from "@/components/QuestionSuggestions/index.vue";
import RelatedQuestions from "@/components/RelatedQuestions/index.vue";
import VoiceInput from "@/components/VoiceInput/index.vue";
import ScrollToBottomButton from "@/components/ScrollToBottomButton/index.vue";
import HistoryConversation from "@/components/HistoryConversation/index.vue";
import Toast from "@/components/Toast/index.vue";

// Mixins
import messageManager from "@/mixins/messageManager.js";
import scrollControl from "@/mixins/scrollControl.js";
import typewriterEffect from "@/mixins/typewriterEffect.js";
import sseHandler from "@/mixins/sseHandler.js";

// API & Utils
import { getUserInfo, sendChatMessage, audioToText } from "@/api/chatApi.js";
import { convertToMP3 } from "@/utils/audioConverter.js";
import { htmlToMarkdown } from "@/utils/htmlToMarkdown.js";

// 常量
import { DEFAULT_SUGGESTIONS } from "@/constants/suggestions.js";

export default {
  name: "App",
  components: {
    ChatHeader,
    ChatBody,
    QuestionSuggestions,
    RelatedQuestions,
    VoiceInput,
    ScrollToBottomButton,
    HistoryConversation,
    Toast,
  },
  mixins: [messageManager, scrollControl, typewriterEffect, sseHandler],
  data() {
    return {
      isAuthenticated: false,
      suggestions: DEFAULT_SUGGESTIONS,
      isStreaming: false,
      // 调试用：保存最后一次转换的 MP3
      lastMp3Blob: null,
      lastMp3Url: null,
      // 调试：显示播放按钮
      showDebugPlayBtn: false,
    };
  },
  methods: {
    /**
     * 发送消息
     * @param {string} content - 消息内容
     */
    sendMessage(content, hasMessage = false) {
      if (this.isStreaming) {
        return;
      }
      
      // 清空相关问题数据
      this.clearRelatedQuestions();
      
      if (!hasMessage) {
        // 添加用户消息
        this.addUserMessage(content);
      }
      // 添加思考中占位符
      this.addThinkingMessage();

      // 触发视图更新并滚动到底部
      this.$forceUpdate();
      this.$nextTick(() => {
        this.scrollToBottom();
      });

      // 关闭之前的 SSE 连接
      this.closeSSEConnection();

      // 重置当前答案
      this.resetCurrentAnswer();

      // 构建请求参数
      const params = {
        inputs: {
          storeCode: "288",
        },
        query: content,
        conversation_id: this.conversation_id,
        user: "me",
      };

      this.isStreaming = true;

      // 发起 SSE 请求
      this.sseCloseConnection = sendChatMessage(params, {
        onMessage: (eventData) => {
          this.handleSSEEvent(eventData);
        },
        onError: (error) => {
          console.error("SSE 连接错误:", error);
          this.addErrorMessage();
          this.$forceUpdate();
          this.isStreaming = false;
        },
        onOpen: () => {
          console.log("SSE 连接已打开");
        },
        onClose: () => {
          console.log("SSE 连接已关闭");
          this.sseCloseConnection = null;
          this.handleScroll();
          this.isStreaming = false;
        },
      });
    },

    /**
     * 处理推荐问题选择
     * @param {object} item - 推荐项
     */
    onSuggestionSelect(item) {
      if (item.text) {
        this.sendMessage(item.text);
      }
    },

    /**
     * 处理相关问题选择
     * @param {object} question - 选中的问题对象 { title: string, content: string }
     */
    onRelatedQuestionSelect(question) {
      if (!question || !question.title || !question.content) {
        console.warn("相关问题数据格式错误", question);
        return;
      }

      // 清空相关问题列表
      this.clearRelatedQuestions();

      // 添加用户消息（显示问题）
      this.addUserMessage(question.title);

      // 将 HTML 格式的 content 转换为 Markdown
      const markdownContent = htmlToMarkdown(question.content);

      // 添加 AI 消息（显示答案）- 使用 isRelatedReply 区分相关问题回复，用于图片一行一个、不固定宽高比
      const aiMessage = {
        id: `related_${Date.now()}`,
        content: markdownContent,
        displayedContent: markdownContent,
        time: new Date().toISOString(),
        sender: "other",
        avatar: "/static/ai-avatar.png",
        isStreaming: false,
        isRelatedReply: true,
      };
      this.messages.push(aiMessage);

      // 触发视图更新并滚动到底部
      this.$forceUpdate();
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    /**
     * 处理发送消息事件
     * @param {string} content - 消息内容
     */
    handleSendMessage(content) {
      this.sendMessage(content);
    },

    /**
     * 处理录音完成事件
     * @param {object} recordingData - 录音数据
     * @param {Blob} recordingData.blob - 录音 Blob 对象
     * @param {string} recordingData.mimeType - 录音的 MIME 类型
     * @param {number} recordingData.duration - 录音时长（毫秒）
     */
    async handleRecordComplete(recordingData) {
      console.log("收到录音数据", recordingData);

      // 检查录音数据是否有效
      if (!recordingData || !recordingData.blob) {
        console.error("录音数据无效");
        return;
      }

      // 检查录音时长（可选：避免发送过短的录音）
      if (recordingData.duration < 500) {
        console.warn("录音时长过短，已忽略");
        this.$refs.toast.show("录音时长过短，请重新录制");
        return;
      }

      // 先添加一个 loading 状态的用户消息
      const loadingMessage = this.addUserMessage("", true);

      // 触发视图更新并滚动到底部
      this.$forceUpdate();
      this.$nextTick(() => {
        this.scrollToBottom();
      });

      try {
        // 1. 转换为 MP3 格式
        console.log("开始转换为 MP3...");
        const mp3Blob = await convertToMP3(recordingData.blob);
        console.log("MP3 转换完成");

        // 调试：保存最后一次转换的 MP3
        if (this.lastMp3Url) {
          URL.revokeObjectURL(this.lastMp3Url); // 释放旧的 URL
        }
        this.lastMp3Blob = mp3Blob;
        this.lastMp3Url = URL.createObjectURL(mp3Blob);

        // 2. 调用语音转文字接口
        const result = await audioToText(mp3Blob, 'audio/mp3');

        // 检查返回结果
        if (result && result.data) {
          console.log("语音识别结果:", result.data);
          // 更新消息为识别出的文字
          this.updateUserMessage(loadingMessage, result.data, false);
          this.$forceUpdate();

          // 自动发送识别出的文字
          this.sendMessage(result.data, true);
        } else {
          // 识别失败，显示错误信息
          this.updateUserMessage(
            loadingMessage,
            "语音识别失败：未返回文字内容",
            false
          );
          this.$forceUpdate();
          console.error("语音识别失败：未识别到文字内容");
        }
      } catch (error) {
        console.error("处理录音失败:", error);

        // 根据错误类型给出友好提示
        let errorMessage = "语音识别失败";
        if (error.message === "音频格式转换失败") {
          errorMessage = "音频转换失败，请重试";
        } else if (error.response) {
          // 服务器返回错误
          errorMessage = `语音识别失败: ${
            error.response.data?.message || error.response.statusText
          }`;
        } else if (error.request) {
          // 请求发送失败
          errorMessage = "网络请求失败，请检查网络连接";
        }

        // 更新消息为错误信息
        this.updateUserMessage(loadingMessage, errorMessage, false);
        this.$forceUpdate();
      }
    },

    /**
     * 调试：播放最后一次转换的 MP3
     */
    playLastMp3() {
      if (!this.lastMp3Url) {
        alert('没有可播放的录音，请先进行一次录音');
        return;
      }
      
      const audio = new Audio(this.lastMp3Url);
      audio.play().catch(error => {
        console.error('播放失败:', error);
        alert('播放失败：' + error.message);
      });
      
      console.log('播放 MP3:', {
        url: this.lastMp3Url,
        size: this.lastMp3Blob.size,
        type: this.lastMp3Blob.type
      });
    },
  },
  async mounted() {
    // 捕获初始视口高度，防止键盘弹出时 100vh 变化导致背景压缩
    document.documentElement.style.setProperty("--app-height", `${window.innerHeight}px`);

    try {
      // 获取用户信息和认证凭证
      // state参数生产和测试环境不一样
      // 生产
      const credentials = await getUserInfo("believe_test", "gh_a194ac4b31d0");
      // 测试
      // const credentials = await getUserInfo("believe_test", "gh_2d0e8fccff98");
      localStorage.setItem("credentials", JSON.stringify(credentials));
      this.isAuthenticated = true;
    } catch (error) {
      console.error("初始化失败", error);
    }
  },
};
</script>
<style lang="scss" scoped="scoped">
html,
body,
#app {
  height: 100%;
}

page {
  height: 100%;
}

.ai-chat {
  height: var(--app-height, 100vh);
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: #f5f5f5;
  background-size: cover;
  background-position: top left;
  background-repeat: no-repeat;
  box-sizing: border-box;
  background-image: url("@/assets/background-new.png");

  .scroll {
    flex: 1;
    overflow-y: auto;
    padding-bottom: 120px;
    height: 100%;
  }

  // 调试按钮样式
  .debug-play-btn {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 9999;
    padding: 10px 15px;
    background-color: #4CAF50;
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 14px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    
    &:hover {
      background-color: #45a049;
    }
    
    &:active {
      transform: scale(0.95);
    }
  }
}
</style>